import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from './environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  expiresAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    roles: string[];
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'sports_academy_token';
  readonly currentUser = signal<AuthResponse['user'] | null>(this.restoreUser());

  constructor(private readonly http: HttpClient) {}

  login(request: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login`, request).pipe(
      tap((response) => {
        localStorage.setItem(this.tokenKey, response.accessToken);
        localStorage.setItem('sports_academy_expires_at', response.expiresAt);
        localStorage.setItem('sports_academy_user', JSON.stringify(response.user));
        this.currentUser.set(response.user);
      })
    );
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('sports_academy_expires_at');
    localStorage.removeItem('sports_academy_user');
    this.currentUser.set(null);
  }

  token(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isLoggedIn(): boolean {
    const token = this.token();
    if (!token || this.isTokenExpired()) {
      this.logout();
      return false;
    }

    return true;
  }

  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('sports_academy_expires_at');
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() <= Date.now();
  }

  private restoreUser(): AuthResponse['user'] | null {
    const raw = localStorage.getItem('sports_academy_user');
    return raw ? JSON.parse(raw) as AuthResponse['user'] : null;
  }
}
